import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend
} from 'recharts';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  LinearProgress
} from '@mui/material';

const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8',
  '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0'
];

const GeographicChart = ({ data }) => {
  const [viewType, setViewType] = React.useState('pie');

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewType(newView);
    }
  };

  if (!data || data.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 300,
        flexDirection: 'column',
        color: 'text.secondary'
      }}>
        <Typography variant="h6" gutterBottom>
          No geographic data available
        </Typography>
        <Typography variant="body2">
          Data will appear once shipments have destinations
        </Typography>
      </Box>
    );
  }

  // Take top 10 destinations for better visualization
  const topDestinations = data.slice(0, 10);
  const totalShipments = topDestinations.reduce((sum, item) => sum + item.shipment_count, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 2, maxWidth: 200 }}>
          <Typography variant="subtitle2" gutterBottom>
            {data.country}
          </Typography>
          {data.city && data.city !== 'Unknown' && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {data.city}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Shipments:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {data.shipment_count}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Delivered:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {data.delivered_count}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Rate:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {data.delivery_rate}%
              </Typography>
            </Box>
          </Box>
        </Paper>
      );
    }
    return null;
  };

  const renderPieChart = () => (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={topDestinations}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={(entry) => `${entry.country} (${entry.shipment_count})`}
          outerRadius={80}
          fill="#8884d8"
          dataKey="shipment_count"
        >
          {topDestinations.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
      </PieChart>
    </ResponsiveContainer>
  );

  const renderBarChart = () => (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={topDestinations}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis 
          dataKey="country" 
          stroke="#666"
          fontSize={12}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis stroke="#666" fontSize={12} />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Bar dataKey="shipment_count" fill="#8884d8" name="Total Shipments" />
        <Bar dataKey="delivered_count" fill="#82ca9d" name="Delivered" />
      </BarChart>
    </ResponsiveContainer>
  );

  const renderListView = () => (
    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
      <List dense>
        {topDestinations.map((destination, index) => (
          <ListItem key={index} sx={{ px: 0 }}>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: COLORS[index % COLORS.length]
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {destination.country}
                  </Typography>
                  {destination.city && destination.city !== 'Unknown' && (
                    <Typography variant="caption" color="text.secondary">
                      , {destination.city}
                    </Typography>
                  )}
                </Box>
              }
              secondary={
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {destination.shipment_count} shipments • {destination.delivered_count} delivered
                    </Typography>
                    <Chip
                      label={`${destination.delivery_rate}%`}
                      size="small"
                      color={destination.delivery_rate >= 80 ? 'success' : destination.delivery_rate >= 60 ? 'warning' : 'error'}
                      variant="outlined"
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={destination.delivery_rate}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: destination.delivery_rate >= 80 ? 'success.main' : destination.delivery_rate >= 60 ? 'warning.main' : 'error.main',
                        borderRadius: 2,
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {((destination.shipment_count / totalShipments) * 100).toFixed(1)}% of total volume
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Top shipping destinations
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Showing top {topDestinations.length} destinations
          </Typography>
        </Box>
        
        <ToggleButtonGroup
          value={viewType}
          exclusive
          onChange={handleViewChange}
          size="small"
        >
          <ToggleButton value="pie">
            Pie
          </ToggleButton>
          <ToggleButton value="bar">
            Bar
          </ToggleButton>
          <ToggleButton value="list">
            List
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewType === 'pie' && renderPieChart()}
      {viewType === 'bar' && renderBarChart()}
      {viewType === 'list' && renderListView()}

      {/* Summary */}
      <Box sx={{ 
        mt: 2, 
        p: 2, 
        bgcolor: 'background.default', 
        borderRadius: 1,
        display: 'flex',
        justifyContent: 'space-around'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="primary">
            {data.length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Countries
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="success.main">
            {totalShipments.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total Shipments
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="info.main">
            {topDestinations[0]?.country || 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Top Destination
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="warning.main">
            {(topDestinations.reduce((sum, item) => sum + item.delivery_rate, 0) / topDestinations.length).toFixed(1)}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Avg Rate
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default GeographicChart;